import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Phone, Mail, UserCheck, UserX, Pencil, UserMinus, UserPlus, Loader2, Calendar } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { agents, type Agent } from '@/lib/api';
import { formatPhone, cn } from '@/lib/utils';
import { CreateAgentModal } from '@/components/CreateAgentModal';
import { EditAgentModal } from '@/components/EditAgentModal';
import { AgentCalendar } from '@/components/AgentCalendar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

export default function Agents() {
  const { token, user } = useAuthStore();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'admin';

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [deactivatingAgent, setDeactivatingAgent] = useState<Agent | null>(null);
  const [reactivatingAgent, setReactivatingAgent] = useState<Agent | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarAgentId, setCalendarAgentId] = useState<string | undefined>(undefined);

  const { data, isLoading } = useQuery({
    queryKey: ['agents', showInactive],
    queryFn: () => agents.list(token!, showInactive),
    enabled: !!token,
  });

  const deactivateMutation = useMutation({
    mutationFn: (agentId: string) => agents.update(token!, agentId, { active: false }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      setDeactivatingAgent(null);
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: (agentId: string) => agents.update(token!, agentId, { active: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      setReactivatingAgent(null);
    },
  });

  // Separate active and inactive agents
  const activeAgents = data?.data?.filter(a => a.active) || [];
  const inactiveAgents = data?.data?.filter(a => !a.active) || [];

  const renderAgentCard = (agent: Agent) => (
    <div
      key={agent.id}
      className={cn(
        'card p-6 transition-shadow',
        agent.active ? 'hover:shadow-md' : 'opacity-60 bg-gray-50'
      )}
    >
      <div className="flex items-start gap-4">
        {/* Avatar */}
        <div className={cn(
          'w-14 h-14 rounded-full flex items-center justify-center text-xl font-semibold',
          agent.active ? 'bg-prima-100 text-prima-700' : 'bg-gray-200 text-gray-500'
        )}>
          {agent.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className={cn(
              'font-semibold truncate',
              agent.active ? 'text-gray-900' : 'text-gray-500'
            )}>
              {agent.name}
            </h3>
            {agent.active ? (
              <span className="badge-green">Virkur</span>
            ) : (
              <span className="badge-gray">Óvirkur</span>
            )}
          </div>

          {/* Specialization */}
          {agent.specialization && (
            <p className="text-xs text-prima-600 mt-0.5">{agent.specialization}</p>
          )}

          <div className="mt-2 space-y-1">
            {agent.phone && (
              <a
                href={`tel:${agent.phone}`}
                className={cn(
                  'flex items-center gap-2 text-sm',
                  agent.active ? 'text-gray-600 hover:text-prima-600' : 'text-gray-400'
                )}
              >
                <Phone className="w-4 h-4" />
                {formatPhone(agent.phone)}
              </a>
            )}
            {agent.email && (
              <a
                href={`mailto:${agent.email}`}
                className={cn(
                  'flex items-center gap-2 text-sm',
                  agent.active ? 'text-gray-600 hover:text-prima-600' : 'text-gray-400'
                )}
              >
                <Mail className="w-4 h-4" />
                {agent.email}
              </a>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-1">
          {/* Calendar button - available to all */}
          {agent.active && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-prima-600 hover:text-prima-700 hover:bg-prima-50"
              onClick={() => {
                setCalendarAgentId(agent.id);
                setShowCalendar(true);
              }}
              title="Sjá dagatal"
            >
              <Calendar className="h-4 w-4" />
            </Button>
          )}
          {/* Admin Actions */}
          {isAdmin && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setEditingAgent(agent)}
                title="Breyta"
              >
                <Pencil className="h-4 w-4" />
              </Button>
              {agent.active ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                  onClick={() => setDeactivatingAgent(agent)}
                  title="Gera óvirkan"
                >
                  <UserMinus className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                  onClick={() => setReactivatingAgent(agent)}
                  title="Endurvirkja"
                >
                  <UserPlus className="h-4 w-4" />
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className={cn(
        'mt-4 pt-4 border-t grid grid-cols-3 gap-4',
        agent.active ? 'border-gray-200' : 'border-gray-100'
      )}>
        <div className="text-center">
          <p className={cn(
            'text-2xl font-bold',
            agent.active ? 'text-gray-900' : 'text-gray-400'
          )}>
            {agent.total_leads || 0}
          </p>
          <p className="text-xs text-gray-500">Leads</p>
        </div>
        <div className="text-center">
          <p className={cn(
            'text-2xl font-bold',
            agent.active ? 'text-green-600' : 'text-gray-400'
          )}>
            {agent.closed_deals || 0}
          </p>
          <p className="text-xs text-gray-500">Lokaðir</p>
        </div>
        <div className="text-center">
          <p className={cn(
            'text-2xl font-bold',
            agent.active ? 'text-prima-600' : 'text-gray-400'
          )}>
            {agent.total_leads && agent.closed_deals
              ? Math.round((agent.closed_deals / agent.total_leads) * 100)
              : 0}%
          </p>
          <p className="text-xs text-gray-500">Árangur</p>
        </div>
      </div>

      {/* Lead acceptance */}
      <div className="mt-4 flex items-center justify-between text-sm">
        <span className="text-gray-500">Tekur við leads:</span>
        {agent.accepts_leads ? (
          <span className={cn(
            'flex items-center gap-1',
            agent.active ? 'text-green-600' : 'text-gray-400'
          )}>
            <UserCheck className="w-4 h-4" />
            Já
            {agent.max_leads_per_day && (
              <span className="text-gray-400">
                (hámark {agent.max_leads_per_day}/dag)
              </span>
            )}
          </span>
        ) : (
          <span className="flex items-center gap-1 text-gray-500">
            <UserX className="w-4 h-4" />
            Nei
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Söluaðilar</h1>
          <p className="text-gray-600 mt-1">
            {activeAgents.length} virkir söluaðilar
            {inactiveAgents.length > 0 && showInactive && (
              <span className="text-gray-400"> / {inactiveAgents.length} óvirkir</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setCalendarAgentId(undefined);
              setShowCalendar(true);
            }}
          >
            <Calendar className="w-4 h-4 mr-2" />
            Dagatal
          </Button>
          {isAdmin && inactiveAgents.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowInactive(!showInactive)}
            >
              {showInactive ? 'Fela óvirka' : `Sýna óvirka (${inactiveAgents.length})`}
            </Button>
          )}
          {isAdmin && (
            <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nýr söluaðili
            </button>
          )}
        </div>
      </div>

      {/* Agent grid */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-prima-600" />
        </div>
      ) : activeAgents.length === 0 && inactiveAgents.length === 0 ? (
        <div className="card p-12 text-center">
          <UserCheck className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Engir söluaðilar skráðir</p>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Active Agents */}
          {activeAgents.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activeAgents.map(renderAgentCard)}
            </div>
          )}

          {/* Inactive Agents Section */}
          {showInactive && inactiveAgents.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-500 flex items-center gap-2">
                <UserX className="w-5 h-5" />
                Óvirkir söluaðilar
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {inactiveAgents.map(renderAgentCard)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Agent Modal */}
      <CreateAgentModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
      />

      {/* Edit Agent Modal */}
      <EditAgentModal
        open={!!editingAgent}
        onOpenChange={(open) => !open && setEditingAgent(null)}
        agent={editingAgent}
      />

      {/* Deactivate Confirmation Dialog */}
      <AlertDialog open={!!deactivatingAgent} onOpenChange={(open) => !open && setDeactivatingAgent(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gera óvirkan?</AlertDialogTitle>
            <AlertDialogDescription>
              Ertu viss um að þú viljir gera <strong>{deactivatingAgent?.name}</strong> óvirkan?
              <br /><br />
              Óvirkur söluaðili:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Getur ekki skráð sig inn</li>
                <li>Fær ekki úthlutað nýjum leads</li>
                <li>Birtist ekki í virkum söluaðilalistum</li>
              </ul>
              <br />
              Þú getur endurvirkjað söluaðilann hvenær sem er.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deactivateMutation.isPending}>
              Hætta við
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deactivatingAgent && deactivateMutation.mutate(deactivatingAgent.id)}
              disabled={deactivateMutation.isPending}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {deactivateMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Gera óvirkan
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reactivate Confirmation Dialog */}
      <AlertDialog open={!!reactivatingAgent} onOpenChange={(open) => !open && setReactivatingAgent(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Endurvirkja söluaðila?</AlertDialogTitle>
            <AlertDialogDescription>
              Ertu viss um að þú viljir endurvirkja <strong>{reactivatingAgent?.name}</strong>?
              <br /><br />
              Virkur söluaðili getur:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Skráð sig inn í kerfið</li>
                <li>Tekið við nýjum leads</li>
                <li>Séð sínar ábendingar og sögu</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reactivateMutation.isPending}>
              Hætta við
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => reactivatingAgent && reactivateMutation.mutate(reactivatingAgent.id)}
              disabled={reactivateMutation.isPending}
              className="bg-green-600 hover:bg-green-700"
            >
              {reactivateMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Endurvirkja
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Agent Calendar Modal */}
      <AgentCalendar
        open={showCalendar}
        onOpenChange={setShowCalendar}
        initialAgentId={calendarAgentId}
      />
    </div>
  );
}
